import * as React from 'react';
import { useTranslation } from 'react-i18next';
import { PortraitPlate } from './portrait-plate';
import { AutoMeetCountdown } from './auto-meet-countdown';
import { initialOf } from './portrait';
import { seloCharacteristic } from './selo';
import { storyFirstName } from '../lib/story';

// Journey wall palette (local copy, like mural.tsx — journey.tsx keeps its
// palette private).
const RED = '#D41920';
const RED_DEEP = '#B5131A';
const PAPER = '#FFF8F5';
const BABY = '#F8D7DD';
const DEK = '#FCE3E0';
const INK = '#1A1018';
const oswald = "'Oswald', sans-serif";
const dmSerif = "'DM Serif Text', serif";

/** How long the slide down onto the wall takes (keep in step with the CSS). */
export const ARRIVAL_SLIDE_MS = 650;

const STYLES = `
@keyframes arrivalIn {
  from { opacity: 0; transform: translateY(-18px) scale(0.98); }
  to { opacity: 1; transform: none; }
}
.arrival-wrap {
  display: grid;
  grid-template-rows: 1fr;
  transition: grid-template-rows ${ARRIVAL_SLIDE_MS}ms cubic-bezier(0.22, 1, 0.36, 1);
}
.arrival-wrap--sliding { grid-template-rows: 0fr; }
.arrival-wrap > div { min-height: 0; overflow: hidden; }
.arrival-card {
  animation: arrivalIn 0.55s cubic-bezier(0.22, 1, 0.36, 1) both;
  transition: transform ${ARRIVAL_SLIDE_MS}ms cubic-bezier(0.22, 1, 0.36, 1), opacity ${ARRIVAL_SLIDE_MS * 0.7}ms ease;
}
.arrival-wrap--sliding .arrival-card { transform: translateY(56px) scale(0.94); opacity: 0; }
.arrival-ghost { cursor: pointer; }
.arrival-ghost:hover { background: rgba(255, 248, 245, 0.12); }
.arrival-card button:focus-visible { outline: 3px solid #F2A0C4; outline-offset: 2px; }
@media (prefers-reduced-motion: reduce) {
  .arrival-wrap, .arrival-card { transition: none; animation: none; }
}
`;

export interface ArrivalUnit {
  unitId: string;
  order: number;
  name: string | null;
  quote?: string | null;
  badge?: string | null;
  classesTotal?: number | null;
  woman?: {
    name: string | null;
    years?: string | null;
    field?: string | null;
    portraitUrl?: string | null;
    symbol?: string | null;
  } | null;
}

/**
 * ArrivalBanner — "she's here": the poster of the unit that just unlocked,
 * held at the top of the wall with the woman big on the right, before it
 * slides down onto its place on the wall. One-time moment per unit.
 *
 * Primary action: meet her (her story, when published and unseen), else start
 * the unit. Secondary: send the poster to the wall now (`onToWall`).
 *
 * With a story to meet, it opens by itself 5 s after the banner is in view
 * (a thin bar fills along the bottom edge). No countdown with reduced motion,
 * while the story is loading, or while the banner is scrolled out of view.
 */
export function ArrivalBanner({
  unit,
  phase,
  isMobile,
  isNarrow,
  busy = false,
  onStart,
  onToWall,
  onMeet,
}: {
  unit: ArrivalUnit;
  /** 'shown' → on screen; 'sliding' → collapsing down onto the wall */
  phase: 'shown' | 'sliding';
  isMobile: boolean;
  /** under 640px: one column, the woman on top */
  isNarrow: boolean;
  busy?: boolean;
  onStart: () => void;
  onToWall: () => void;
  /** her story is published and unseen: the primary action opens it */
  onMeet?: () => void;
}) {
  const { t } = useTranslation('p2e');
  const womanName = unit.woman?.name ?? null;
  const stacked = isNarrow;
  const plateSize = stacked ? (isMobile ? 220 : 260) : 250;
  const seal = seloCharacteristic(unit.badge, unit.name);
  const classesTotal = unit.classesTotal ?? 0;

  const primaryLabel = onMeet
    ? t('story.banner.meet', { name: storyFirstName(womanName).toUpperCase() })
    : t('journey.startThisUnit');
  const secondaryLabel = onMeet ? t('journey.arrival.later') : t('journey.arrival.toWall');
  // Her picture is the way into her story too (it pulses to say so).
  const meetAria = onMeet
    ? t('story.portrait.openNew', { name: womanName ?? '' })
    : undefined;

  const pill = (text: React.ReactNode, solid = false) => (
    <span
      style={{
        fontFamily: oswald,
        fontSize: 10.5,
        fontWeight: 600,
        letterSpacing: '0.18em',
        textTransform: 'uppercase',
        border: `1.5px solid ${solid ? INK : 'rgba(255,248,245,0.65)'}`,
        background: solid ? INK : 'transparent',
        color: PAPER,
        borderRadius: 999,
        padding: '4px 11px',
        whiteSpace: 'nowrap',
      }}
    >
      {text}
    </span>
  );

  return (
    <div
      className={`arrival-wrap${phase === 'sliding' ? ' arrival-wrap--sliding' : ''}`}
      data-testid="arrival-banner"
      aria-hidden={phase === 'sliding' ? true : undefined}
    >
      <div>
        <style dangerouslySetInnerHTML={{ __html: STYLES }} />
        <section
          className="arrival-card"
          aria-label={t('journey.arrival.kicker', { n: unit.order + 1 })}
          style={{
            position: 'relative',
            margin: isMobile ? '18px 0 10px' : '24px 0 14px',
            backgroundColor: RED,
            backgroundImage: `radial-gradient(rgba(26,16,24,.14) 1px, transparent 1.5px), linear-gradient(180deg, ${RED}, ${RED_DEEP})`,
            backgroundSize: '9px 9px, 100% 100%',
            color: PAPER,
            boxShadow: '0 24px 60px rgba(120, 40, 40, 0.22)',
            padding: stacked
              ? isMobile
                ? '22px 18px 26px'
                : '28px 26px 30px'
              : '34px 30px 34px 34px',
            display: 'grid',
            gridTemplateColumns: stacked ? '1fr' : `minmax(0, 1fr) ${plateSize + 20}px`,
            gap: stacked ? 10 : 22,
            alignItems: 'center',
            textAlign: stacked ? 'center' : 'left',
            overflow: 'visible',
          }}
        >
          {/* tape strips on the top edge */}
          <div
            aria-hidden
            style={{
              position: 'absolute',
              top: -7,
              left: stacked ? 26 : 54,
              width: stacked ? 78 : 104,
              height: 20,
              background: 'rgba(248, 215, 221, 0.6)',
              transform: 'rotate(-6deg)',
            }}
          />
          <div
            aria-hidden
            style={{
              position: 'absolute',
              top: -7,
              right: stacked ? 26 : 110,
              width: stacked ? 78 : 104,
              height: 20,
              background: 'rgba(248, 215, 221, 0.6)',
              transform: 'rotate(5deg)',
            }}
          />

          {/* the woman, big (first on a phone) */}
          {stacked && (
            <div style={{ display: 'flex', justifyContent: 'center', marginBottom: 6 }}>
              <PortraitPlate
                url={unit.woman?.portraitUrl}
                name={womanName}
                initial={initialOf(womanName ?? unit.name, 'E')}
                size={plateSize}
                onOpen={onMeet}
                openLabel={meetAria}
                storySeen={false}
              />
            </div>
          )}

          <div style={{ minWidth: 0, display: 'flex', flexDirection: 'column', alignItems: stacked ? 'center' : 'flex-start' }}>
            <div style={{ display: 'flex', alignItems: 'center', gap: 10, marginBottom: 12, flexWrap: 'wrap', justifyContent: stacked ? 'center' : 'flex-start' }}>
              <span
                style={{
                  fontFamily: oswald,
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  background: PAPER,
                  color: RED,
                  padding: '3px 9px',
                }}
              >
                {t('journey.arrival.unit', { n: unit.order + 1 })}
              </span>
              <span
                style={{
                  fontFamily: oswald,
                  fontSize: 10.5,
                  fontWeight: 600,
                  letterSpacing: '0.2em',
                  textTransform: 'uppercase',
                  color: BABY,
                }}
              >
                {t('journey.arrival.here')}
              </span>
            </div>

            <h2
              style={{
                fontFamily: oswald,
                textTransform: 'uppercase',
                fontWeight: 700,
                fontSize: stacked ? (isMobile ? 32 : 40) : 44,
                lineHeight: 0.96,
                margin: '0 0 12px',
                color: PAPER,
                letterSpacing: '0.005em',
              }}
            >
              {unit.name}
            </h2>

            {unit.quote && (
              <p
                style={{
                  fontFamily: dmSerif,
                  fontStyle: 'italic',
                  fontSize: stacked ? 15 : 17,
                  lineHeight: 1.45,
                  color: DEK,
                  margin: '0 0 8px',
                  maxWidth: '42ch',
                }}
              >
                &ldquo;{unit.quote}&rdquo;
              </p>
            )}
            {womanName && (
              <p
                style={{
                  fontFamily: oswald,
                  fontSize: 10.5,
                  fontWeight: 700,
                  letterSpacing: '0.16em',
                  textTransform: 'uppercase',
                  color: BABY,
                  margin: '0 0 18px',
                }}
              >
                &mdash; {womanName}
                {unit.woman?.years ? (
                  <span style={{ fontFamily: dmSerif, fontStyle: 'italic', fontWeight: 400, letterSpacing: 0, textTransform: 'none', color: DEK }}>
                    {' '}· {unit.woman.years}
                  </span>
                ) : null}
              </p>
            )}

            <div style={{ display: 'flex', gap: 8, flexWrap: 'wrap', marginBottom: 22, justifyContent: stacked ? 'center' : 'flex-start' }}>
              {classesTotal > 0 && pill(t('journey.arrival.classCount', { count: classesTotal }))}
              {seal && pill(<>{t('journey.arrival.seal', { characteristic: seal })} {unit.woman?.symbol ?? ''}</>, true)}
            </div>

            <div style={{ display: 'flex', gap: 10, flexWrap: 'wrap', justifyContent: stacked ? 'center' : 'flex-start' }}>
              <button
                type="button"
                className="tactile-btn"
                data-testid="arrival-primary"
                onClick={onMeet ?? onStart}
                disabled={busy}
                style={
                  {
                    display: 'inline-flex',
                    alignItems: 'center',
                    gap: 12,
                    background: PAPER,
                    color: RED,
                    padding: '13px 24px',
                    minHeight: 44,
                    fontFamily: oswald,
                    fontSize: 11.5,
                    fontWeight: 700,
                    letterSpacing: '0.18em',
                    textTransform: 'uppercase',
                    border: 'none',
                    opacity: busy ? 0.7 : 1,
                    ['--tactile-shadow' as string]: '0 5px 0 rgba(120, 8, 14, 0.95), 0 8px 16px rgba(0,0,0,0.18)',
                    ['--tactile-shadow-hover' as string]: '0 6px 0 rgba(120, 8, 14, 1), 0 10px 20px rgba(0,0,0,0.22)',
                    ['--tactile-shadow-press' as string]: '0 1px 0 rgba(120, 8, 14, 0.95), 0 2px 6px rgba(0,0,0,0.14)',
                  } as React.CSSProperties
                }
              >
                {primaryLabel} <span aria-hidden style={{ fontSize: 15, lineHeight: 1 }}>&rarr;</span>
              </button>
              <button
                type="button"
                className="arrival-ghost"
                data-testid="arrival-to-wall"
                onClick={onToWall}
                disabled={busy}
                style={{
                  border: '1px solid rgba(255, 248, 245, 0.5)',
                  background: 'transparent',
                  fontFamily: oswald,
                  fontWeight: 700,
                  letterSpacing: '0.16em',
                  fontSize: 11,
                  textTransform: 'uppercase',
                  padding: '0 16px',
                  minHeight: 44,
                  color: PAPER,
                }}
              >
                {secondaryLabel} <span aria-hidden>&darr;</span>
              </button>
            </div>
          </div>

          {onMeet && phase === 'shown' && !busy && (
            <AutoMeetCountdown onFire={onMeet} color={PAPER} threshold={0.6} />
          )}

          {!stacked && (
            <div style={{ position: 'relative', justifySelf: 'end', paddingRight: 6 }}>
              <PortraitPlate
                url={unit.woman?.portraitUrl}
                name={womanName}
                initial={initialOf(womanName ?? unit.name, 'E')}
                size={plateSize}
                onOpen={onMeet}
                openLabel={meetAria}
                storySeen={false}
              />
            </div>
          )}
        </section>
      </div>
    </div>
  );
}
